import * as THREE from "three";

/**
 * Sun-shadow frustum fitting (issue 121).
 *
 * Three's `DirectionalLightShadow` ships a default
 * `OrthographicCamera(-5, 5, 5, -5)`. Nothing in the rig ever replaced
 * it, so every cast shadow was clipped to ±5 m around the light target
 * — at golden hour (~10° elevation) a 2.2 m sauna throws a ~12 m
 * shadow, which meant the building read as if it were floating.
 *
 * This module fits the ortho box in LIGHT SPACE, which is what makes
 * the geometry tractable — and worth spelling out, because it is not
 * what the issue assumed:
 *
 * - **left/right/top/bottom come from the model box alone.** The
 *   ground projection of a caster under a directional light runs along
 *   the light ray, which IS the shadow camera's depth axis, so a
 *   projected corner has exactly the same light-space x/y as the
 *   corner it came from. A 12 m golden-hour shadow therefore never
 *   widens the box — there is no "shadow tail" in x/y that could be
 *   traded for texel density, and cropping x/y could only ever cut the
 *   building itself.
 * - **near/far is where a low sun costs.** The projection pushed the
 *   required far plane to ~33 m at golden hour and ~44 m at night on a
 *   20 m² + deck build, against the 32 m the rig hardcoded.
 * - What *does* grow the box at a low sun is the model box's own
 *   light-space footprint: as the light lies down, the ground plane
 *   tilts into the ortho plane (measured top 4.96 m → 6.40 m from
 *   midday to golden hour). Both are past the ±5 m default.
 *
 * Fitting in light space also keeps the fit correct for an off-centre
 * model (deck, stairs) without moving `light.target`: moving the
 * target would rotate the light direction and desynchronise the cast
 * shadow from the sun disc `SceneSky` paints from the same
 * `sunPosition`.
 *
 * Cost: pure math over 16 points at most. Called on model rebuild
 * (dirty-tracking) and while the time-of-day lerp actually moves the
 * light — never unconditionally per frame.
 */

/** Light-space fit for a `DirectionalLightShadow` ortho camera. */
export interface ShadowFrustumFit {
	left: number;
	right: number;
	top: number;
	bottom: number;
	near: number;
	far: number;
	/** Shadow-map texels per world metre across the widest frustum axis. */
	texelsPerMeter: number;
	/**
	 * True when the shadow-length budget truncated the far plane, i.e.
	 * the very tip of a near-horizon shadow is not drawn. The model is
	 * never cropped.
	 */
	tailClipped: boolean;
	/** Normal bias matched to the fitted texel size (peter-panning guard). */
	normalBias: number;
}

export interface FitShadowFrustumOptions {
	/** World-space bounds of the shadow casters (the sauna model). */
	bounds: THREE.Box3 | null;
	/** Directional light position (its direction is position → target). */
	lightPosition: readonly [number, number, number];
	/** Light target; the rig leaves this at the origin. */
	lightTarget?: readonly [number, number, number];
	/** World Y of the ground plane the shadow lands on. */
	groundY?: number;
	/** Square shadow-map resolution from the runtime profile. */
	shadowMapSize: number;
	/** Shadow-length budget; the far plane is cropped past this. */
	maxShadowLengthM?: number;
}

/**
 * Shadow-length budget, in metres travelled along the light ray.
 *
 * Sized from the issue's own worst case — 20 m² + deck at golden hour —
 * measured rather than guessed: the padded model tops out at 4.56 m and
 * the sun sits at 10°, so the ray runs 29.4 m to the default ground
 * (−0.55 m) and 32.0 m to the lowest configurable one (900 mm plinths,
 * shallow burial). 34 m clears both, which is what keeps that case
 * uncropped as the acceptance criteria require.
 *
 * It still crops the night extreme: a 6° moon key would run ~48 m, and
 * the far end of that is both invisible (the grass plane and the tone
 * curve have long since swallowed it) and expensive in depth precision
 * — a wider near/far range is what turns `bias` into peter-panning on
 * the plinths.
 */
export const DEFAULT_MAX_SHADOW_LENGTH_M = 34;

/** Slack around the model box, in metres. */
const PADDING_M = 0.6;

/** Matches the pre-issue-121 hardcoded values; used when bounds are unknown. */
const FALLBACK_HALF_EXTENT_M = 6;

const MIN_NORMAL_BIAS = 0.01;
const MAX_NORMAL_BIAS = 0.05;

function normalBiasForTexelSize(texelsPerMeter: number): number {
	if (!Number.isFinite(texelsPerMeter) || texelsPerMeter <= 0) {
		return MAX_NORMAL_BIAS;
	}
	return THREE.MathUtils.clamp(
		(1 / texelsPerMeter) * 1.4,
		MIN_NORMAL_BIAS,
		MAX_NORMAL_BIAS,
	);
}

function boxCorners(bounds: THREE.Box3): THREE.Vector3[] {
	const { min, max } = bounds;
	const corners: THREE.Vector3[] = [];
	for (const x of [min.x, max.x]) {
		for (const y of [min.y, max.y]) {
			for (const z of [min.z, max.z]) {
				corners.push(new THREE.Vector3(x, y, z));
			}
		}
	}
	return corners;
}

function fallbackFit(shadowMapSize: number): ShadowFrustumFit {
	const span = FALLBACK_HALF_EXTENT_M * 2;
	const texelsPerMeter = shadowMapSize > 0 ? shadowMapSize / span : 0;
	return {
		left: -FALLBACK_HALF_EXTENT_M,
		right: FALLBACK_HALF_EXTENT_M,
		top: FALLBACK_HALF_EXTENT_M,
		bottom: -FALLBACK_HALF_EXTENT_M,
		near: 0.5,
		far: 32,
		texelsPerMeter,
		tailClipped: false,
		normalBias: normalBiasForTexelSize(texelsPerMeter),
	};
}

/**
 * Fit a directional-light shadow ortho box to the model, and its depth
 * range to the model plus the ground projection of that model along
 * the light direction.
 */
export function fitDirectionalShadowFrustum({
	bounds,
	lightPosition,
	lightTarget = [0, 0, 0],
	groundY = 0,
	shadowMapSize,
	maxShadowLengthM = DEFAULT_MAX_SHADOW_LENGTH_M,
}: FitShadowFrustumOptions): ShadowFrustumFit {
	if (!bounds || bounds.isEmpty()) return fallbackFit(shadowMapSize);

	const eye = new THREE.Vector3(...lightPosition);
	const focus = new THREE.Vector3(...lightTarget);
	if (eye.distanceTo(focus) < 1e-4) return fallbackFit(shadowMapSize);

	// Light space: the shadow camera sits at `eye` looking at `focus`.
	const view = new THREE.Matrix4()
		.lookAt(eye, focus, new THREE.Vector3(0, 1, 0))
		.setPosition(eye)
		.invert();

	// Pad the model box, then take its light-space AABB. That box IS the
	// ortho plane — see the module header: a projected corner shares its
	// caster's light-space x/y, so the shadow can only move `far`.
	const padded = bounds.clone().expandByScalar(PADDING_M);
	const modelLight = padded.clone().applyMatrix4(view);
	const left = modelLight.min.x;
	const right = modelLight.max.x;
	const bottom = modelLight.min.y;
	const top = modelLight.max.y;

	// Depth: light space looks down -z, so a point's distance from the
	// light is -z, and travelling `d` along the light ray adds exactly `d`
	// to that distance. The far plane is the deepest ground hit; the
	// budget crops the tip of a near-horizon shadow.
	const lightDir = focus.clone().sub(eye).normalize();
	let deepest = -modelLight.min.z;
	let tailClipped = false;
	if (lightDir.y < -1e-3) {
		for (const corner of boxCorners(padded)) {
			const distance = (groundY - corner.y) / lightDir.y;
			if (distance <= 0) continue;
			if (distance > maxShadowLengthM) tailClipped = true;
			const travel = Math.min(distance, maxShadowLengthM);
			const cornerDepth = -corner.clone().applyMatrix4(view).z;
			deepest = Math.max(deepest, cornerDepth + travel);
		}
	}

	const near = Math.max(0.1, -modelLight.max.z - PADDING_M);
	const far = Math.max(near + 1, deepest + PADDING_M);

	const widestSpan = Math.max(right - left, top - bottom);
	const texelsPerMeter =
		shadowMapSize > 0 && widestSpan > 0 ? shadowMapSize / widestSpan : 0;

	return {
		left,
		right,
		top,
		bottom,
		near,
		far,
		texelsPerMeter,
		tailClipped,
		normalBias: normalBiasForTexelSize(texelsPerMeter),
	};
}

/** Push a fit onto a live light, refreshing the projection + shadow map. */
export function applyShadowFrustum(
	light: THREE.DirectionalLight,
	fit: ShadowFrustumFit,
): void {
	const camera = light.shadow?.camera as THREE.OrthographicCamera | undefined;
	if (!camera) return;
	const shadow = light.shadow;
	// Three r183 reallocates on shadow TYPE changes, but not mapSize changes.
	// A quality switch otherwise samples a buffer with the previous dimensions.
	if (
		shadow.map &&
		(shadow.map.width !== shadow.mapSize.x || shadow.map.height !== shadow.mapSize.y)
	) {
		shadow.dispose();
		shadow.map = null;
		shadow.mapPass = null;
	}
	camera.left = fit.left;
	camera.right = fit.right;
	camera.top = fit.top;
	camera.bottom = fit.bottom;
	camera.near = fit.near;
	camera.far = fit.far;
	camera.updateProjectionMatrix();
	light.shadow.normalBias = fit.normalBias;
	light.shadow.needsUpdate = true;
}

/**
 * World-space bounds of everything under `root` that actually casts a
 * shadow. Measured from the built scene rather than derived from
 * `BuildInput`, so deck, stairs, chimney and roof overhang are all
 * included without a second geometry model to keep in sync.
 *
 * `castShadow` is the predicate the renderer itself uses, so filtering on
 * it is correct by construction: a mesh it excludes contributes nothing
 * to the shadow map and cannot under-fit the box. It also keeps the
 * interaction and opening-guide helpers out — they are wall-sized
 * near-transparent planes that would inflate the box and cost texels —
 * without this module having to know their names.
 */
export function measureShadowCasterBounds(
	root: THREE.Object3D | null | undefined,
): THREE.Box3 | null {
	if (!root) return null;
	const box = new THREE.Box3();
	let found = false;

	root.updateWorldMatrix(true, true);
	root.traverse((object) => {
		const mesh = object as THREE.Mesh;
		if (!mesh.isMesh || !mesh.castShadow) return;
		// expandByObject refreshes the mesh's own world matrix.
		box.expandByObject(mesh);
		found = true;
	});
	return found ? box : null;
}
